import type { VoidComponent } from "solid-js";
import InputModal from "../../../components/modal/InputModal";
import { type InferInput, object } from "valibot";
import { TextInput } from "../../../components/inputs/TextInput";
import UserService from "../../../services/UserService";
import type { User } from "../../../state/user";
import { useCustomerState } from "../../../state/customer";
import { toUserSettingsDto } from "../../../utils/mappers/user";
import { requireText } from "../../../utils/validation/base";
import { MAX_CHARACTERS_SM } from "../../../constants";
import { useModalManager } from "../../../components/modal/ModalManager";
import GenericFailureModal from "../../GenericFailureModal";
import { useTranslation } from "../../../state/i18n";

type EditUserNameModalProps = {
  user: User;
};

export const editUserNameSchema = object({
  firstName: requireText(MAX_CHARACTERS_SM),
  lastName: requireText(MAX_CHARACTERS_SM),
});

const EditUserNameModal: VoidComponent<EditUserNameModalProps> = (props) => {
  const { t } = useTranslation();
  const customer = useCustomerState();
  const { replace } = useModalManager();

  const onConfirm = (value: InferInput<typeof editUserNameSchema>) => {
    const user = props.user;
    return new Promise((resolve, reject) =>
      UserService.updateCustomerUserSettings(customer.customerId, user.id).mutate(
        toUserSettingsDto({ ...user, ...value }),
        {
          onSuccess: resolve,
          onError: (err) => {
            replace({
              content: () => <GenericFailureModal title={t(($) => $.EDIT_USER_NAME_FAILURE_MODAL_TITLE)} error={err} />,
            });
            reject(err);
          },
        },
      ),
    );
  };

  return (
    <InputModal
      variant="default"
      title={t(($) => $.EDIT_USER_NAME_MODAL_TITLE)}
      titleIcon="pencilAlt"
      confirmLabel={t(($) => $.EDIT_USER_NAME_MODAL_SAVE_CHANGES)}
      cancelLabel={t(($) => $.CANCEL_BUTTON)}
      onConfirm={onConfirm}
      initialValue={props.user}
      schema={editUserNameSchema}
    >
      {({ value, updateValue, error }) => (
        <div class="flex flex-col gap-4">
          <TextInput
            label={t(($) => $.EDIT_USER_NAME_MODAL_FIRST_NAME)}
            value={value("firstName")}
            required
            onBlur={(e) => {
              updateValue("firstName", e.target.value);
            }}
            onInput={(e) => {
              updateValue("firstName", e.target.value);
            }}
            errorMessage={error("firstName")}
          />
          <TextInput
            label={t(($) => $.EDIT_USER_NAME_MODAL_LAST_NAME)}
            value={value("lastName")}
            required
            onBlur={(e) => {
              updateValue("lastName", e.target.value);
            }}
            onInput={(e) => {
              updateValue("lastName", e.target.value);
            }}
            errorMessage={error("lastName")}
          />
        </div>
      )}
    </InputModal>
  );
};

export default EditUserNameModal;
